// OwnerAddOffer.js
import { useState } from "react";
import { BACKEND_URL } from "../config";
import { useNavigate } from "react-router-dom";
import { safeGetItem } from "../utils/storage";

export default function OwnerAddOffer() {
  const token = safeGetItem("token");
  const [consultancyId, setConsultancyId] = useState(""); // will auto-fill from owner/me
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [discount, setDiscount] = useState(0);
  const [validTill, setValidTill] = useState("");
  const [offerType, setOfferType] = useState("Scholarship");
  const [topOffer, setTopOffer] = useState(false);
  const [boosting, setBoosting] = useState({
    highlightOnHomepage: false,
    sendToStudents: false,
    promoteOnTop: false,
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const offerTypes = ["Scholarship", "Discount", "Freebie", "Limited Time", "Featured"];
  const navigate = useNavigate();

  const submit = async () => {
    if (!token) {
      alert("Please log in again.");
      navigate("/login");
      return;
    }

    if (!handleValidation()) return;

    try {
      // Always work with a local variable so we don't depend on async setState
      let id = consultancyId;

      if (!id) {
        // fetch owner consultancy id if not provided
        const r = await fetch(`${BACKEND_URL}/consultancies/owner/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!r.ok) {
          alert("Could not find your consultancy. Please check with admin.");
          return;
        }

        const data = await r.json();
        id = data._id;
        setConsultancyId(id);
      }

      setStatus("loading");
      const res = await fetch(`${BACKEND_URL}/consultancies/${id}/offers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          description,
          discount,
          validTill,
          offerType,
          topOffer,
          highlightOnHomepage: boosting.highlightOnHomepage,
          sendToStudents: boosting.sendToStudents,
          promoteOnTop: boosting.promoteOnTop,
        }),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        // show backend message if available
        alert(body.error || "Error adding offer");
        setStatus("idle");
        return;
      }

      setStatus("success");
      setTimeout(() => {
        navigate("/owner/dashboard");
      }, 1200);
    } catch (err) {
      console.error("Add offer error", err);
      alert("Unexpected error adding offer");
      setStatus("idle");
    }
  };

  const handleBoostToggle = (field) => {
    setBoosting((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const clearAll = () => {
    setTitle("");
    setDescription("");
    setDiscount(0);
    setValidTill("");
    setOfferType("Scholarship");
    setTopOffer(false);
    setBoosting({
      highlightOnHomepage: false,
      sendToStudents: false,
      promoteOnTop: false,
    });
    setErrors({});
  };

  const livePreview = () => (
    <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow-xl text-white">
      <div className="flex items-center gap-2">
        <span className="text-2xl">🎁</span>
        <div>
          <p className="text-xs uppercase tracking-[5px] text-white/60">
            Live Offer Preview
          </p>
          <h3 className="text-xl font-semibold">Student View</h3>
        </div>
      </div>
      <div className="mt-6 rounded-3xl border border-white/10 bg-gradient-to-br from-blue-600/40 to-purple-600/30 p-5">
        <div className="flex items-center justify-between">
          <p className="text-lg font-bold">{title || "Untitled Offer"}</p>
          {topOffer && (
            <span className="px-3 py-1 rounded-full text-xs bg-amber-400/20 border border-amber-300/40 text-amber-100 font-semibold">
              TOP OFFER
            </span>
          )}
        </div>
        <p className="mt-3 text-sm text-white/80">
          {description || "Students will see your compelling description here."}
        </p>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <span className="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 font-semibold text-sm">
            {discount}% OFF
          </span>
          <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs text-white/80">
            Type: {offerType}
          </span>
          <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs text-white/80">
            Valid till: {validTill ? new Date(validTill).toLocaleDateString("en-GB") : "TBD"}
          </span>
        </div>
      </div>
    </div>
  );

  const handleValidation = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Offer title is required.";
    if (!description.trim()) newErrors.description = "Description is required.";
    if (!validTill) newErrors.validTill = "Select an expiry date.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 pb-40 text-white">
      <div className="max-w-6xl mx-auto px-4 py-10 space-y-8">
        <div className="flex items-center gap-2 text-sm text-white/60">
          <button onClick={() => navigate("/owner/dashboard")} className="hover:text-white">
            Owner Dashboard
          </button>
          <span>›</span>
          <span>Offers Lab</span>
          <span>›</span>
          <span className="text-white font-medium">Create Offer</span>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow-[0_20px_70px_rgba(15,23,42,0.7)]">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <span className="text-4xl">🎯</span>
              <div>
                <p className="text-sm uppercase tracking-[6px] text-white/60">
                  Offers Laboratory
                </p>
                <h1 className="text-3xl font-semibold">
                  Design powerful offers to attract more students
                </h1>
              </div>
            </div>
            <p className="text-white/70">
              Experiment with scholarship drops, spotlight perks, and limited-time campaigns.
            </p>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs uppercase tracking-[4px] w-fit">
              Premium Feature
            </span>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr,0.95fr]">
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 shadow-xl space-y-5">
            <div>
              <label className="flex items-center gap-2 text-sm text-white/70">
                <span>🪄</span> Offer Title
              </label>
              <div className="relative mt-1">
                <input
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setErrors((prev) => ({ ...prev, title: undefined }));
                  }}
                  placeholder=" "
                  className="peer w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm placeholder-transparent focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/50"
                />
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-sm transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-white/50 peer-focus:top-2 peer-focus:text-xs peer-focus:text-blue-200">
                  Enter offer title
                </span>
              </div>
              {errors.title && <p className="text-xs text-rose-300 mt-1">{errors.title}</p>}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-white/70">
                <span>📝</span> Offer Description
              </label>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setErrors((prev) => ({ ...prev, description: undefined }));
                }}
                placeholder="Tell students why they should grab this offer..."
                className="w-full min-h-[140px] rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/50"
              />
              {errors.description && (
                <p className="text-xs text-rose-300 mt-1">{errors.description}</p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-white/70">
                <span>💰</span> Discount
              </label>
              <div className="flex items-center gap-4 mt-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={discount}
                  onChange={(e) => setDiscount(Number(e.target.value))}
                  className="flex-1 accent-blue-400"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={discount}
                    onChange={(e) =>
                      setDiscount(Math.min(100, Math.max(0, Number(e.target.value) || 0)))
                    }
                    className="w-20 text-center rounded-2xl border border-white/10 bg-white/5 p-2 text-sm"
                  />
                  <span>%</span>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm text-white/70">
                  <span>📅</span> Offer Valid Till
                </label>
                <input
                  type="date"
                  value={validTill}
                  onChange={(e) => {
                    setValidTill(e.target.value);
                    setErrors((prev) => ({ ...prev, validTill: undefined }));
                  }}
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/50"
                />
                {errors.validTill && (
                  <p className="text-xs text-rose-300 mt-1">{errors.validTill}</p>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm text-white/70">
                  <span>🏷️</span> Offer Type
                </label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {offerTypes.map((type) => (
                    <button
                      type="button"
                      key={type}
                      onClick={() => setOfferType(type)}
                      className={`px-3 py-2 rounded-full text-xs font-semibold ${
                        offerType === type
                          ? "bg-gradient-to-r from-blue-500 to-indigo-500"
                          : "bg-white/5"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl p-3">
              <div>
                <p className="text-sm font-semibold">⭐ Top Offer</p>
                <p className="text-xs text-white/70">
                  Spotlight this offer across your public profile
                </p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={topOffer}
                  onChange={() => setTopOffer((prev) => !prev)}
                  className="sr-only"
                />
                <div className={`h-6 w-12 rounded-full transition ${topOffer ? "bg-green-400" : "bg-white/20"}`}>
                  <div
                    className={`h-5 w-5 rounded-full bg-white shadow transform transition ${
                      topOffer ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </div>
              </label>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3">Offer Boosting Tools</h3>
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <label className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={boosting.highlightOnHomepage}
                    onChange={() => handleBoostToggle("highlightOnHomepage")}
                  />
                  🔥 Highlight on homepage
                </label>
                <label className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={boosting.sendToStudents}
                    onChange={() => handleBoostToggle("sendToStudents")}
                  />
                  📧 Send to students
                </label>
                <label className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={boosting.promoteOnTop}
                    onChange={() => handleBoostToggle("promoteOnTop")}
                  />
                  🏆 Promote on top
                </label>
              </div>
            </div>
          </div>

          {livePreview()}
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-4 sm:pb-6">
        <div className="mx-auto flex max-w-4xl flex-col gap-3 rounded-2xl border border-white/10 bg-slate-950/85 p-3 shadow-[0_20px_60px_rgba(15,23,42,0.7)] backdrop-blur sm:rounded-3xl sm:p-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-white/80 flex items-center gap-2">
            {status === "success"
              ? "✅ Offer Published"
              : status === "loading"
              ? "⏳ Publishing..."
              : "🎯 Ready to launch"}
          </div>
          <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-3 sm:flex sm:flex-wrap sm:gap-3">
            <button
              onClick={clearAll}
              className="min-h-[44px] rounded-2xl border border-white/20 px-4 py-3 text-sm font-semibold"
            >
              ♻ Clear All
            </button>
            <button
              onClick={submit}
              disabled={status === "loading"}
              className="min-h-[44px] rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold disabled:opacity-60"
            >
              💾 Save as Draft
            </button>
            <button
              onClick={submit}
              disabled={status === "loading"}
              className="min-h-[44px] rounded-2xl bg-gradient-to-r from-emerald-400 to-green-500 px-5 py-3 text-sm font-semibold text-slate-900 shadow disabled:opacity-60"
            >
              {status === "loading" ? "Publishing..." : "🚀 Create & Publish"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
